import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2, Minus, X, LogOut,
  ClipboardList, BookOpen, ChevronLeft, Search, Plus, Trash2,
} from 'lucide-react';
import { getSession, clearSession } from '../components/ProtectedRoute.jsx';
import Registre from './Registre.jsx';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatData(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString('ca-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ── Estat badge ───────────────────────────────────────────────────────────────

function EstatBadge({ estat }) {
  if (estat === 'firmat') {
    return (
      <span className="inline-flex items-center gap-1.5 text-green-600 font-medium text-sm">
        <CheckCircle2 size={15} /> Firmat
      </span>
    );
  }
  if (estat === 'sense_firma') {
    return (
      <span className="inline-flex items-center gap-1.5 text-red-600 font-medium text-sm">
        <X size={15} /> Sense firma
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-gray-400 font-medium text-sm">
      <Minus size={15} /> Pendent
    </span>
  );
}

// ── Toast ─────────────────────────────────────────────────────────────────────

function Toast({ message, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-green-600 text-white px-6 py-3 rounded-xl shadow-lg text-sm font-medium flex items-center gap-2">
      <CheckCircle2 size={16} />
      {message}
    </div>
  );
}

// ── Modal base (overlay) ──────────────────────────────────────────────────────

function Overlay({ children, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6"
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

// ── SenseFirmaModal ───────────────────────────────────────────────────────────

function SenseFirmaModal({ comerc, onClose, onSuccess }) {
  const [motiu, setMotiu] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const session = getSession();

  async function handleConfirmar() {
    if (!motiu.trim()) { setError('El motiu és obligatori'); return; }
    setLoading(true);
    try {
      const res = await fetch(`/api/comercos/${comerc.id}/sense-firma`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ motiu: motiu.trim(), nom_enquestador: session?.enquestador_nom }),
      });
      if (!res.ok) { setError('Error en guardar'); setLoading(false); return; }
      const today = new Date().toISOString().split('T')[0];
      onSuccess({ id: comerc.id, estat: 'sense_firma', data_visita: today, motiu_sense_firma: motiu.trim() });
    } catch {
      setError('Error de connexió');
      setLoading(false);
    }
  }

  return (
    <Overlay onClose={onClose}>
      <h2 className="text-base font-bold text-dark mb-1">Sense firma</h2>
      <p className="text-sm text-gray-500 mb-4">{comerc.nom_comercial}</p>

      <label className="block text-sm font-medium text-dark mb-1">Indica el motiu:</label>
      <textarea
        value={motiu}
        onChange={e => { setMotiu(e.target.value); setError(''); }}
        placeholder="Motiu de la no signatura..."
        autoFocus
        rows={3}
        className="w-full rounded-lg px-3 py-2 text-sm text-dark resize-none outline-none mb-1"
        style={{ border: '1px solid #DF273F' }}
      />
      {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
      {!error && <div className="mb-3" />}

      <div className="flex gap-3">
        <button
          onClick={handleConfirmar}
          disabled={loading}
          className="flex-1 py-3 rounded-lg text-white font-semibold text-sm min-h-[44px]"
          style={{ backgroundColor: '#DF273F' }}
        >
          {loading ? 'Guardant...' : 'Confirmar'}
        </button>
        <button
          onClick={onClose}
          className="flex-1 py-3 rounded-lg border border-gray-300 text-gray-600 font-semibold text-sm min-h-[44px]"
        >
          Cancel·lar
        </button>
      </div>
    </Overlay>
  );
}

// ── ComercModal ───────────────────────────────────────────────────────────────

function ComercModal({ comerc, onClose, onSenseFirma }) {
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [senseFirma, setSenseFirma] = useState(false);

  useEffect(() => {
    if (!comerc) return;
    fetch(`/api/comercos/${comerc.id}`)
      .then(r => r.json())
      .then(setDetail)
      .catch(() => setDetail(comerc));
  }, [comerc?.id]);

  if (!comerc) return null;
  const c = detail || comerc;

  // Flujo sense firma obert
  if (senseFirma) {
    return (
      <SenseFirmaModal
        comerc={c}
        onClose={onClose}
        onSuccess={updated => {
          onSenseFirma(updated);
          onClose();
        }}
      />
    );
  }

  // Modal confirmació re-enquesta (firmat)
  if (confirm) {
    return (
      <Overlay onClose={() => setConfirm(false)}>
        <h2 className="text-base font-bold text-dark mb-3">Estàs segur?</h2>
        <p className="text-sm text-gray-600 mb-5">
          Totes les dades guardades es sobreescriuran. Estàs segur?
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => navigate(`/app/formularis/${c.id}`)}
            className="flex-1 py-3 rounded-lg text-white font-semibold text-sm min-h-[44px]"
            style={{ backgroundColor: '#DF273F' }}
          >
            Sí, continua
          </button>
          <button
            onClick={() => setConfirm(false)}
            className="flex-1 py-3 rounded-lg border border-gray-300 text-gray-600 font-semibold text-sm min-h-[44px]"
          >
            No, torna enrere
          </button>
        </div>
      </Overlay>
    );
  }

  // Modal principal
  return (
    <Overlay onClose={onClose}>
      <h2 className="text-base font-bold text-dark mb-1">{c.nom_comercial}</h2>
      <p className="text-sm text-gray-500 mb-3">{c.adreca || '—'}</p>

      {/* Pendent */}
      {c.estat === 'pendent' && (
        <>
          <p className="text-sm text-dark font-medium mb-4">
            Què vols fer amb {c.nom_comercial}?
          </p>
          <div className="flex flex-col gap-2">
            <button
              onClick={() => navigate(`/app/formularis/${c.id}`)}
              className="w-full py-3 rounded-lg text-white font-semibold text-sm min-h-[44px]"
              style={{ backgroundColor: '#DF273F' }}
            >
              Omplir formulari
            </button>
            <button
              onClick={() => setSenseFirma(true)}
              className="w-full py-3 rounded-lg font-semibold text-sm min-h-[44px] border"
              style={{ borderColor: '#DF273F', color: '#DF273F' }}
            >
              Sense firma
            </button>
            <button
              onClick={onClose}
              className="w-full py-3 rounded-lg border border-gray-300 text-gray-600 font-semibold text-sm min-h-[44px]"
            >
              Cancel·lar
            </button>
          </div>
        </>
      )}

      {/* Firmat */}
      {c.estat === 'firmat' && (
        <>
          <p className="text-sm text-gray-600 mb-4">
            Aquest comerç ja ha firmat el document el{' '}
            <strong>{formatData(c.data_visita)}</strong>.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setConfirm(true)}
              className="flex-1 py-3 rounded-lg text-white font-semibold text-sm min-h-[44px]"
              style={{ backgroundColor: '#444444' }}
            >
              Tornar a fer el formulari
            </button>
            <button
              onClick={onClose}
              className="flex-1 py-3 rounded-lg border border-gray-300 text-gray-600 font-semibold text-sm min-h-[44px]"
            >
              Cancel·lar
            </button>
          </div>
        </>
      )}

      {/* Sense firma */}
      {c.estat === 'sense_firma' && (
        <>
          <p className="text-sm text-gray-600 mb-2">
            Aquest comerç no ha volgut firmar el{' '}
            <strong>{formatData(c.data_visita)}</strong>.
          </p>
          {c.motiu_sense_firma && (
            <p className="text-sm text-gray-600 mb-4">
              Motiu: <strong>{c.motiu_sense_firma}</strong>
            </p>
          )}
          <div className="flex flex-col gap-2">
            <button
              onClick={() => navigate(`/app/formularis/${c.id}`)}
              className="w-full py-3 rounded-lg text-white font-semibold text-sm min-h-[44px]"
              style={{ backgroundColor: '#DF273F' }}
            >
              Omplir formulari
            </button>
            <button
              onClick={() => setSenseFirma(true)}
              className="w-full py-3 rounded-lg font-semibold text-sm min-h-[44px] border"
              style={{ borderColor: '#DF273F', color: '#DF273F' }}
            >
              Actualitzar sense firma
            </button>
            <button
              onClick={onClose}
              className="w-full py-3 rounded-lg border border-gray-300 text-gray-600 font-semibold text-sm min-h-[44px]"
            >
              Cancel·lar
            </button>
          </div>
        </>
      )}
    </Overlay>
  );
}

// ── Tab Enquestes ─────────────────────────────────────────────────────────────

function TabEnquestes() {
  const navigate = useNavigate();
  const [comercos, setComercos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('nom_comercial');
  const [sortDir, setSortDir] = useState('asc');
  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);   // comerc a esborrar
  const [deleteConfirm, setDeleteConfirm] = useState(false); // 2a confirmació

  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    fetch('/api/comercos')
      .then(r => r.json())
      .then(data => { setComercos(data); setLoading(false); })
      .catch(() => { setError('Error carregant les dades.'); setLoading(false); });
  }, []);

  function toggleSort(key) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  }

  function handleSenseFirma(updated) {
    setComercos(prev => prev.map(c =>
      c.id === updated.id ? { ...c, ...updated } : c
    ));
    setToast('Visita registrada sense firma');
  }

  async function handleDelete(comerc) {
    await fetch(`/api/comercos/${comerc.id}`, { method: 'DELETE' });
    setComercos(prev => prev.filter(c => c.id !== comerc.id));
    setDeleteTarget(null);
    setDeleteConfirm(false);
    setToast(`"${comerc.nom_comercial}" eliminat`);
  }

  const filtered = comercos
    .filter(c =>
      !debouncedSearch ||
      c.nom_comercial.toLowerCase().includes(debouncedSearch.toLowerCase())
    )
    .sort((a, b) => {
      let va = a[sortKey] ?? '';
      let vb = b[sortKey] ?? '';
      if (sortKey === 'data_visita') { va = va || '0'; vb = vb || '0'; }
      const cmp = String(va).localeCompare(String(vb), 'ca', { sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const counts = {
    firmat: comercos.filter(c => c.estat === 'firmat').length,
    pendent: comercos.filter(c => c.estat === 'pendent').length,
    sense_firma: comercos.filter(c => c.estat === 'sense_firma').length,
  };

  function SortIcon({ col }) {
    if (sortKey !== col) return <span className="text-gray-300 ml-1">↕</span>;
    return <span className="ml-1" style={{ color: '#DF273F' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  }

  if (loading) return <div className="flex justify-center pt-20 text-gray-400">Carregant...</div>;
  if (error) return <div className="flex justify-center pt-20 text-red-500">{error}</div>;

  return (
    <div>
      {/* Stats */}
      <div className="flex gap-3 mb-5 flex-wrap items-center">
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-2 text-sm">
          <CheckCircle2 size={15} className="text-green-600" />
          <span className="text-green-700 font-semibold">{counts.firmat}</span>
          <span className="text-green-600">firmats</span>
        </div>
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-sm">
          <Minus size={15} className="text-gray-400" />
          <span className="text-gray-600 font-semibold">{counts.pendent}</span>
          <span className="text-gray-500">pendents</span>
        </div>
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-sm">
          <X size={15} className="text-red-500" />
          <span className="text-red-600 font-semibold">{counts.sense_firma}</span>
          <span className="text-red-500">sense firma</span>
        </div>
        <button
          onClick={() => navigate('/app/formularis/nou')}
          className="ml-auto flex items-center gap-2 rounded-xl text-white font-semibold text-sm px-4 min-h-[44px] flex-shrink-0"
          style={{ backgroundColor: '#DF273F' }}
        >
          <Plus size={15} />
          Nou comerci
        </button>
      </div>

      {/* Search amb lupa */}
      <div className="relative mb-4">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
        />
        <input
          type="search"
          placeholder="Cercar per nom comercial..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-3 rounded-lg bg-white text-sm text-gray-700 outline-none min-h-[44px]"
          style={{ border: '1px solid #DF273F' }}
        />
      </div>

      {/* Taula */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
          <button
            className="col-span-6 text-left flex items-center hover:text-dark transition-colors min-h-[44px]"
            onClick={() => toggleSort('nom_comercial')}
          >
            Nom comercial <SortIcon col="nom_comercial" />
          </button>
          <button
            className="col-span-3 text-left flex items-center hover:text-dark transition-colors min-h-[44px]"
            onClick={() => toggleSort('estat')}
          >
            Estat <SortIcon col="estat" />
          </button>
          <button
            className="col-span-3 text-left flex items-center hover:text-dark transition-colors min-h-[44px]"
            onClick={() => toggleSort('data_visita')}
          >
            Data visita <SortIcon col="data_visita" />
          </button>
        </div>

        {filtered.length === 0 ? (
          <div className="px-4 py-10 text-center text-gray-400 text-sm">
            Cap comerç trobat
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {filtered.map(c => (
              <div
                key={c.id}
                className="grid grid-cols-12 gap-2 px-4 w-full hover:bg-gray-50 transition-colors min-h-[44px] items-center"
              >
                <button
                  onClick={() => setSelected(c)}
                  className="col-span-5 text-sm text-dark font-medium truncate text-left py-3"
                >
                  {c.nom_comercial}
                </button>
                <span className="col-span-3"><EstatBadge estat={c.estat} /></span>
                <span className="col-span-3 text-sm text-gray-400">{formatData(c.data_visita)}</span>
                <button
                  onClick={e => { e.stopPropagation(); setDeleteTarget(c); setDeleteConfirm(false); }}
                  className="col-span-1 flex justify-end items-center text-gray-300 hover:text-red-500 transition-colors p-2"
                  title="Eliminar comerç"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {filtered.length > 0 && (
        <p className="text-xs text-gray-400 mt-2 text-right">
          {filtered.length} resultats{debouncedSearch ? ` per "${debouncedSearch}"` : ''}
        </p>
      )}

      {selected && (
        <ComercModal
          comerc={selected}
          onClose={() => setSelected(null)}
          onSenseFirma={updated => {
            handleSenseFirma(updated);
            setSelected(null);
          }}
        />
      )}

      {/* Modal eliminació — 1a confirmació */}
      {deleteTarget && !deleteConfirm && (
        <Overlay onClose={() => setDeleteTarget(null)}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <Trash2 size={18} className="text-red-500" />
            </div>
            <h2 className="text-base font-bold text-dark">Eliminar comerç</h2>
          </div>
          <p className="text-sm text-gray-600 mb-1">
            Estàs a punt d'eliminar:
          </p>
          <p className="text-sm font-semibold text-dark mb-4">
            {deleteTarget.nom_comercial}
          </p>
          <p className="text-sm text-gray-500 mb-5">
            Aquesta acció eliminarà el comerç del llistat de formularis i del registre de visites. No es pot desfer.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setDeleteConfirm(true)}
              className="flex-1 py-3 rounded-lg text-white font-semibold text-sm min-h-[44px]"
              style={{ backgroundColor: '#DF273F' }}
            >
              Continuar
            </button>
            <button
              onClick={() => setDeleteTarget(null)}
              className="flex-1 py-3 rounded-lg border border-gray-300 text-gray-600 font-semibold text-sm min-h-[44px]"
            >
              Cancel·lar
            </button>
          </div>
        </Overlay>
      )}

      {/* Modal eliminació — 2a confirmació */}
      {deleteTarget && deleteConfirm && (
        <Overlay onClose={() => { setDeleteTarget(null); setDeleteConfirm(false); }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <Trash2 size={18} className="text-red-500" />
            </div>
            <h2 className="text-base font-bold text-dark">Segur que vols eliminar-lo?</h2>
          </div>
          <p className="text-sm text-gray-600 mb-5">
            Es eliminarà <strong>{deleteTarget.nom_comercial}</strong> permanentment. Aquesta acció <strong>no es pot desfer</strong>.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => handleDelete(deleteTarget)}
              className="flex-1 py-3 rounded-lg text-white font-semibold text-sm min-h-[44px]"
              style={{ backgroundColor: '#DF273F' }}
            >
              Sí, eliminar
            </button>
            <button
              onClick={() => { setDeleteTarget(null); setDeleteConfirm(false); }}
              className="flex-1 py-3 rounded-lg border border-gray-300 text-gray-600 font-semibold text-sm min-h-[44px]"
            >
              No, cancel·lar
            </button>
          </div>
        </Overlay>
      )}

      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
    </div>
  );
}

// ── Pantalla inici ────────────────────────────────────────────────────────────

function PantallaInici({ onSelect }) {
  return (
    <div className="flex flex-col items-center justify-center flex-1 px-6 py-12 gap-6">
      <button
        onClick={() => onSelect('enquestes')}
        className="w-full max-w-sm bg-white rounded-2xl shadow-md border-2 hover:shadow-lg active:scale-95 transition-all flex flex-col items-center gap-4 py-10 px-6"
        style={{ borderColor: '#DF273F' }}
      >
        <ClipboardList size={48} style={{ color: '#DF273F' }} />
        <span className="text-xl font-bold text-dark">Formularis</span>
        <span className="text-sm text-gray-400 text-center">Llista de comerços i recollida de consentiments</span>
      </button>

      <button
        onClick={() => onSelect('registre')}
        className="w-full max-w-sm bg-white rounded-2xl shadow-md border-2 border-gray-200 hover:shadow-lg active:scale-95 transition-all flex flex-col items-center gap-4 py-10 px-6"
      >
        <BookOpen size={48} className="text-gray-400" />
        <span className="text-xl font-bold text-dark">Registre de visites</span>
        <span className="text-sm text-gray-400 text-center">Historial de visites realitzades</span>
      </button>
    </div>
  );
}


// ── Main App ──────────────────────────────────────────────────────────────────

export default function AppMain() {
  const navigate = useNavigate();
  const session = getSession();
  const [vista, setVista] = useState('inici');

  function handleLogout() {
    clearSession();
    navigate('/login', { replace: true });
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-dark text-white px-4 sticky top-0 z-40 shadow-md" style={{ minHeight: '56px' }}>
        <div className="max-w-5xl mx-auto flex items-center justify-between" style={{ minHeight: '56px' }}>
          <div className="flex items-center gap-3 flex-shrink-0">
            {vista !== 'inici' ? (
              <button
                onClick={() => setVista('inici')}
                className="flex items-center gap-1.5 text-sm text-gray-300 hover:text-white transition-colors min-h-[44px] pr-2"
              >
                <ChevronLeft size={18} />
                <span className="hidden sm:inline">Inici</span>
              </button>
            ) : (
              <img
                src="/logomontcadayreixac.png"
                alt="Ajuntament de Montcada i Reixac"
                className="w-auto object-contain" style={{ height: '72px' }}
              />
            )}
          </div>

          <span className="font-semibold absolute left-1/2 -translate-x-1/2 pointer-events-none" style={{ fontSize: '1.5rem' }}>
            {vista === 'inici' && <span className="hidden sm:inline">Cens de Comerços</span>}
            {vista === 'enquestes' && 'Formularis'}
            {vista === 'registre' && 'Registre de visites'}
          </span>

          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="text-sm text-gray-300 hidden sm:block">{session?.enquestador_nom}</span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-sm px-3 rounded-lg border border-gray-500 hover:border-white hover:text-white text-gray-300 transition-colors min-h-[44px]"
            >
              <LogOut size={14} />
              <span className="hidden sm:inline">Tancar sessió</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 flex flex-col max-w-5xl w-full mx-auto">
        {vista === 'inici' && <PantallaInici onSelect={setVista} />}
        {vista === 'enquestes' && (
          <main className="px-4 py-6">
            <TabEnquestes />
          </main>
        )}
        {vista === 'registre' && <Registre onBack={() => setVista('inici')} />}
      </div>
    </div>
  );
}
